import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { type AudienceOptionType } from '@/api/audience';
import AudienceBaseForm, { audienceBaseFormFields } from '@/app/audiences/AudienceBaseForm';
import { useTypedAppFormContext } from '@/components/Form';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from '@/components/Form/fields';
import { RadioGroupItem } from '@/components/Form/switches';
import { Panel } from '@/components/panels';
import { Text } from '@/components/typography';
import { AudienceSelect } from '@/features/inputs';

export const AI_AUDIENCE_PROFILE = {
  CRM: 'crm',
  CUSTOM: 'custom',
} as const;

type AIAudienceProfileType = (typeof AI_AUDIENCE_PROFILE)[keyof typeof AI_AUDIENCE_PROFILE];

export type CreateAIAudienceFormValues = {
  name: string;
  description: string;
  profile: AIAudienceProfileType;
  audience: AudienceOptionType | null;
  includeSampleCompanies: boolean;
};

export const createAIAudienceDefaultValues: CreateAIAudienceFormValues = {
  name: '',
  description: '',
  profile: AI_AUDIENCE_PROFILE.CRM,
  audience: null,
  includeSampleCompanies: false,
};

const CreateAIAudienceContent = () => {
  const form = useTypedAppFormContext({
    defaultValues: createAIAudienceDefaultValues,
  });
  const audienceRequiredSchema = z
    .looseObject({
      id: z.string().min(1, t`Audience is required`),
    })
    .nullable()
    .refine((audience) => audience != null && audience.id.length > 0, {
      message: t`Audience is required`,
    });

  return (
    <Panel title={<Trans>AI Audience Settings</Trans>}>
      <div>
        <Trans>
          <Text variant="label">Note: </Text>
          <Text variant="body1">
            AI audiences are built to include up to 2,000 accounts based on your sample. If the full
            set isn't available, you'll receive all matches we find.
          </Text>
        </Trans>
      </div>

      <AudienceBaseForm
        form={form}
        fields={audienceBaseFormFields}
        audience={undefined}
        autoFocus
      />

      <form.AppField name="profile">
        {(field) => (
          <FieldSet className="w-full max-w-md">
            <FieldLegend variant="label">
              <Trans>Training Profile</Trans>
            </FieldLegend>
            <field.RadioGroup className="w-full">
              <FieldLabel htmlFor="ai-audience-profile-crm">
                <field.Field className="cursor-pointer" orientation="horizontal">
                  <FieldContent>
                    <FieldTitle>
                      <Trans>CRM Profile</Trans>
                    </FieldTitle>
                    <FieldDescription>
                      <Trans>
                        C99 will model an audience based on the companies you have opened
                        opportunities with in the past.
                      </Trans>
                    </FieldDescription>
                  </FieldContent>
                  <RadioGroupItem id="ai-audience-profile-crm" value={AI_AUDIENCE_PROFILE.CRM} />
                </field.Field>
              </FieldLabel>

              <FieldLabel htmlFor="ai-audience-profile-custom">
                <field.Field className="cursor-pointer" orientation="horizontal">
                  <FieldContent>
                    <FieldTitle>
                      <Trans>Custom Profile</Trans>
                    </FieldTitle>
                    <FieldDescription>
                      <Trans>
                        Choose from an existing audience to train the model which will build your
                        new AI audience.
                      </Trans>
                    </FieldDescription>
                  </FieldContent>
                  <RadioGroupItem
                    id="ai-audience-profile-custom"
                    value={AI_AUDIENCE_PROFILE.CUSTOM}
                  />
                </field.Field>
              </FieldLabel>
            </field.RadioGroup>
            <field.Error />
          </FieldSet>
        )}
      </form.AppField>

      <form.Subscribe
        selector={(state) => ({ profile: state.values.profile, isSubmitting: state.isSubmitting })}
      >
        {({ profile, isSubmitting }) =>
          profile === AI_AUDIENCE_PROFILE.CUSTOM && (
            <form.AppField
              name="audience"
              validators={{
                onSubmit: audienceRequiredSchema,
              }}
            >
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;

                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor="ai-audience-select">
                      <Trans>Audience</Trans>
                    </FieldLabel>
                    <AudienceSelect
                      id="ai-audience-select"
                      className="w-full max-w-md"
                      disabled={isSubmitting}
                      aria-invalid={hasError || undefined}
                      value={field.state.value ?? undefined}
                      onChange={(value) => field.handleChange(value ?? null)}
                      onBlur={field.handleBlur}
                    />
                    {hasError && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                );
              }}
            </form.AppField>
          )
        }
      </form.Subscribe>

      <form.AppField name="includeSampleCompanies">
        {(field) => (
          <field.Field orientation="horizontal">
            <field.Switch id="ai-audience-include-sample-companies" />
            <FieldContent>
              <FieldLabel htmlFor="ai-audience-include-sample-companies">
                <Trans>Include sample companies</Trans>
              </FieldLabel>
              <FieldDescription>
                <Trans>Include the training companies in the resulting audience.</Trans>
              </FieldDescription>
              <field.Error />
            </FieldContent>
          </field.Field>
        )}
      </form.AppField>
    </Panel>
  );
};

export default CreateAIAudienceContent;
